import { AttributeEnum, ProfileType } from "../types/profile";
import { RoleEnum } from "../types/invitation.types";

/* Family composition at signup: the study's DATA-ONLY subject roles (is_authenticative
 * === false, e.g. Enfant) flagged auto_create_enabled must be declared ("how many?") by
 * the first registering family member; the rows are created server-side at consent.
 * Shared by the progress-bar spine and the HomeContent navigation — one predicate,
 * never desynchronized (the NIR-step pattern). */
export const getAutoCreateTargetRoles = (
  role?: ProfileType | null,
  allRoles?: ProfileType[] | null
): ProfileType[] => {
  // Only authenticative subject registrants declare a composition.
  if (!role || role.attributes?.[AttributeEnum.IS_AUTHENTICATIVE] === false) return [];
  if (role.name !== undefined && role.name !== RoleEnum.SUBJECT) return [];
  return (allRoles ?? []).filter(
    (candidate) =>
      candidate.name === RoleEnum.SUBJECT &&
      candidate.id != null &&
      candidate.attributes?.[AttributeEnum.IS_AUTHENTICATIVE] === false &&
      candidate.attributes?.auto_create_enabled === true
  );
};

/* Absence declaration at signup: the subject roles this registrant may declare absent
 * from the dossier (« Y a-t-il un second parent ? »), i.e. every SUBJECT role whose
 * absence_declarable_by_study_role_id is the registrant's role. Same registrant guard
 * as the count question (authenticative subject); the question itself is rendered on
 * the FAMILY_COMPOSITION step, before the count questions. */
export const getAbsenceDeclarableRoles = (
  role?: ProfileType | null,
  allRoles?: ProfileType[] | null
): ProfileType[] => {
  if (!role || role.id == null) return [];
  if (role.attributes?.[AttributeEnum.IS_AUTHENTICATIVE] === false) return [];
  if (role.name !== undefined && role.name !== RoleEnum.SUBJECT) return [];
  return (allRoles ?? []).filter(
    (candidate) =>
      candidate.name === RoleEnum.SUBJECT &&
      candidate.id != null &&
      candidate.attributes?.absence_declarable_by_study_role_id === role.id
  );
};

// The FAMILY_COMPOSITION step exists when there is at least one count question OR one
// absence question to ask — the single predicate the progress-bar spine
// (helper/inscriptionSteps.ts) and the HomeContent navigation both consume.
export const shouldDeclareFamilyComposition = (
  role?: ProfileType | null,
  allRoles?: ProfileType[] | null
): boolean =>
  getAutoCreateTargetRoles(role, allRoles).length +
    getAbsenceDeclarableRoles(role, allRoles).length >
  0;

/* Investigator dossier management (« Ajouter un dossier ») — unlike the signup step,
 * the médecin is authoritative: ALL subject roles of the study are addable, data-only
 * or authenticative (the latter become credential-less claimable placeholders). */
export const getManageableSubjectRoles = (
  allRoles?: ProfileType[] | null
): ProfileType[] =>
  (allRoles ?? []).filter(
    (candidate) => candidate.name === RoleEnum.SUBJECT && candidate.id != null
  );

// The dossier feature exists only where families exist: the study has at least one
// data-only subject role. Capability-gated — MentalPlus-style studies never see it.
export const canManageDossiers = (
  role?: ProfileType | null,
  allRoles?: ProfileType[] | null
): boolean =>
  role?.name === RoleEnum.INVESTIGATOR &&
  (allRoles ?? []).some(
    (candidate) =>
      candidate.name === RoleEnum.SUBJECT &&
      candidate.attributes?.[AttributeEnum.IS_AUTHENTICATIVE] === false
  );
